import * as FileSaver from 'file-saver'
import moment from 'moment'
import * as XLSX from 'xlsx'
import { capitalizeEveryWord } from 'helpers'
import { newFormatThousandSeparator } from 'utils/formatNumber'

export const handleDownloadExcel = async (
  {
    inventories = [],
    summaryItems = [],
    processedProcesses = [],
    configData,
    totalInput = 0,
    totalOutput = 0,
    outputLoss = 0,
  },
  setIsDownload,
  setFirstRows,
  setTotalRows,
  dashboardId,
) => {
  setIsDownload(false)
  setFirstRows(0)
  setTotalRows(100)
  setIsDownload(true)

  try {
    const fileType =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    const bookType = 'xlsx'
    const matrix = []

    const writeCell = (r, c, val) => {
      while (matrix.length <= r) matrix.push([])
      while (matrix[r].length <= c) matrix[r].push('')
      matrix[r][c] = val
    }

    let currentColOffset = 0
    const merges = []

    // 1. OUTPUT BLOCK
    writeCell(0, currentColOffset, 'Tabel Ringkasan (OUTPUT)')
    merges.push({
      s: { r: 0, c: currentColOffset },
      e: { r: 0, c: currentColOffset + 3 },
    })

    writeCell(1, currentColOffset, 'Jenis Barang')
    writeCell(1, currentColOffset + 1, 'Kode Inventori')
    writeCell(1, currentColOffset + 2, 'Lot ID')
    writeCell(1, currentColOffset + 3, 'Jumlah')

    let rOut = 2
    summaryItems.forEach((out) => {
      writeCell(rOut, currentColOffset, out?.item?.nameItem || out?.itemName || '-')
      writeCell(rOut, currentColOffset + 1, out?.ownerCode || '-')
      writeCell(rOut, currentColOffset + 2, out?.lotCode || '-')
      writeCell(
        rOut,
        currentColOffset + 3,
        `${newFormatThousandSeparator(out?.total ?? out?.amount ?? 0)} ${capitalizeEveryWord(
          out?.unit ?? '',
        )}`,
      )
      rOut++
    })

    writeCell(rOut + 1, currentColOffset, 'Total Keluar')
    writeCell(rOut + 1, currentColOffset + 3, newFormatThousandSeparator(totalOutput))
    writeCell(rOut + 2, currentColOffset, 'Konversi Loss')
    writeCell(rOut + 2, currentColOffset + 3, `${outputLoss}%`)

    currentColOffset += 5 // Spacer column

    // 2. PROCESS BLOCKS
    processedProcesses.forEach((proc, idx) => {
      writeCell(0, currentColOffset, proc.processingName || `Proses ${idx + 1}`)
      merges.push({
        s: { r: 0, c: currentColOffset },
        e: { r: 0, c: currentColOffset + 1 },
      })

      writeCell(1, currentColOffset, 'User')
      writeCell(1, currentColOffset + 1, 'Jumlah')

      let rProc = 2
      const users = proc.userData || []
      users.forEach((u) => {
        writeCell(rProc, currentColOffset, u.userName || '-')
        writeCell(rProc, currentColOffset + 1, newFormatThousandSeparator(u.total || 0))
        rProc++
      })

      writeCell(rProc + 1, currentColOffset, 'Total Proses')
      writeCell(rProc + 1, currentColOffset + 1, newFormatThousandSeparator(proc.totalProc || 0))
      writeCell(rProc + 2, currentColOffset, 'Konversi Loss')
      writeCell(rProc + 2, currentColOffset + 1, `${proc.loss || 0}%`)

      currentColOffset += 3 // Spacer
    })

    // 3. INPUT BLOCK
    writeCell(
      0,
      currentColOffset,
      `Daftar Inventori: ${configData?.startUser?.name ?? '-'} (INPUT)`,
    )
    merges.push({
      s: { r: 0, c: currentColOffset },
      e: { r: 0, c: currentColOffset + 4 },
    })

    writeCell(1, currentColOffset, 'Jenis Barang')
    writeCell(1, currentColOffset + 1, 'Kode Inventori')
    writeCell(1, currentColOffset + 2, 'Lot ID')
    writeCell(1, currentColOffset + 3, 'Tanggal')
    writeCell(1, currentColOffset + 4, 'Jumlah')

    let rIn = 2
    inventories.forEach((inv) => {
      writeCell(
        rIn,
        currentColOffset,
        inv.item?.nameItem ?? inv.comodity?.name ?? inv.commodityName ?? '-',
      )
      writeCell(rIn, currentColOffset + 1, inv.ownerCode ?? '-')

      const lotIdStr = inv.lotData?.length > 0
        ? inv.lotData.map((lot) => lot.lotCode).filter(Boolean).join(', ') || '-'
        : '-'
      writeCell(rIn, currentColOffset + 2, lotIdStr)
      writeCell(rIn, currentColOffset + 3, moment(inv.createdAt).format('DD MMM YYYY, HH:mm'))
      writeCell(
        rIn,
        currentColOffset + 4,
        `${newFormatThousandSeparator(inv.initialAmount ?? inv.amount ?? 0)} ${capitalizeEveryWord(
          inv.unit ?? inv.item?.unit ?? '',
        )}`,
      )
      rIn++
    })

    writeCell(rIn + 1, currentColOffset, 'Total Masuk')
    writeCell(rIn + 1, currentColOffset + 4, newFormatThousandSeparator(totalInput))
    writeCell(rIn + 2, currentColOffset, 'Konversi Loss')
    writeCell(rIn + 2, currentColOffset + 4, '0%')

    // Simulate progress
    for (let i = 0; i <= 100; i += 25) {
      setFirstRows(i)
      await new Promise((r) => setTimeout(r, 10))
    }

    const sheet = XLSX.utils.aoa_to_sheet(matrix)
    if (merges.length > 0) sheet['!merges'] = merges

    // Set col widths dynamically
    const cols = []
    for (let i = 0; i < currentColOffset + 5; i++) {
        cols.push({ wch: 18 }) // general size accommodating text and amounts
    }
    sheet['!cols'] = cols

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, sheet, 'Dashboard Ketelusuran')

    const excelBuffer = XLSX.write(wb, { bookType, type: 'array' })
    const finalData = new Blob([excelBuffer], { type: fileType })
    FileSaver.saveAs(
      finalData,
      `Dashboard_Ketelusuran_${dashboardId}_${moment().format('YYYY-MM-DD')}.xlsx`,
    )
  } catch (error) {
    console.error('Failed to download Excel', error)
  } finally {
    setIsDownload(false)
  }
}
